// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// actions
import { RaaActions } from '../actions';

// apis
import { riskAssessmentApprovalApi } from '../apis/riskAssessmentApproval.api';

// models
import type {
  ReqRAApprovalFormProps,
  ReqRAApprovalProps,
  ResRAApprovalHistoryProps,
  ResRAApprovalProps
} from '../models';
import type { ResultError } from 'modules/common';

/**
 * 위험성평가 결재 목록 조회
 * @param data - 검색 조건
 */
export const getRAApproval = createAsyncThunk<
  ResRAApprovalProps,
  ReqRAApprovalProps,
  {
    rejectValue: ResultError;
  }
>(RaaActions.GET_RISK_ASSESSMENT_APPROVAL, async (data, { fulfillWithValue, rejectWithValue }) => {
  try {
    const res = await riskAssessmentApprovalApi.getRAApproval(data);

    return fulfillWithValue({
      ...res
    });
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

/**
 * 위험성평가 결재 이력 조회
 * @param riskAssessmentId - 위험성평가 ID
 */
export const getRAApprovalHistory = createAsyncThunk<
  ResRAApprovalHistoryProps,
  number,
  {
    rejectValue: ResultError;
  }
>(
  RaaActions.GET_RISK_ASSESSMENT_APPROVAL_HISTORY,
  async (riskAssessmentId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await riskAssessmentApprovalApi.getRAApprovalHistory(riskAssessmentId);

      return fulfillWithValue({
        ...res
      });
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 결재 상신 API
 * @param data - 결재 상신 데이터
 */
export const patchSubmitRAApproval = createAsyncThunk<
  void,
  ReqRAApprovalFormProps,
  {
    rejectValue: ResultError;
  }
>(RaaActions.PATCH_RISK_ASSESSMENT_APPROVAL_SUBMIT, async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('submit', JSON.stringify(data.submit));
    formData.append('file', data.file);

    await riskAssessmentApprovalApi.submitRAApproval(formData);
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

/**
 * 결재 회수 API
 * @param riskAssessmentId - 위험성평가 ID
 */
export const patchRetrieveRAApproval = createAsyncThunk<void, number, { rejectValue: ResultError }>(
  RaaActions.PATCH_RISK_ASSESSMENT_APPROVAL_RETRIEVE,
  async (riskAssessmentId, { rejectWithValue }) => {
    try {
      await riskAssessmentApprovalApi.retrieveRAApproval(riskAssessmentId);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 결재 반려 API
 * @param data - 결재 반려 데이터
 */
export const patchDenyRAApproval = createAsyncThunk<
  void,
  ReqRAApprovalFormProps,
  { rejectValue: ResultError }
>(RaaActions.PATCH_RISK_ASSESSMENT_APPROVAL_DENY, async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('approval', JSON.stringify(data.submit));
    formData.append('file', data.file);

    await riskAssessmentApprovalApi.denyRAApproval(formData);
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

/**
 * 결재 승인 API
 * @param data - 결재 승인 데이터
 */
export const patchApproveRAApproval = createAsyncThunk<
  void,
  ReqRAApprovalFormProps,
  { rejectValue: ResultError }
>(RaaActions.PATCH_RISK_ASSESSMENT_APPROVAL_APPROVE, async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('approval', JSON.stringify(data.submit));
    formData.append('file', data.file);

    await riskAssessmentApprovalApi.approveRAApproval(formData);
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});
